import { useCallback, useEffect, useRef } from "react";
import {
  createConversation,
  fetchConversation,
  openRunStream,
  type ChatCallError,
} from "@/features/intelligence/api/client";
import {
  hydrateThread,
  newThreadState,
  type ThreadState,
} from "@/features/intelligence/lib/thread-state";
import { forgetRun, recallRun } from "./runtime";
import type { ApplyThread, DriveStream, LoadState, MutableRef, StateSetter } from "./types";

export function useConversationLifecycle({
  locale,
  conversationId,
  onConversationCreated,
  activeIdRef,
  abortRef,
  threadRef,
  setActiveId,
  setThreadState,
  setThreadError,
  setActionError,
  setCanReconnect,
  applyThread,
  drive,
  resetFrontendTools,
  resetApprovalOutcome,
}: {
  locale: "sv" | "en";
  conversationId: string | null;
  onConversationCreated?: (id: string) => void;
  activeIdRef: MutableRef<string | null>;
  abortRef: MutableRef<AbortController | null>;
  threadRef: MutableRef<ThreadState>;
  setActiveId: StateSetter<string | null>;
  setThreadState: StateSetter<LoadState>;
  setThreadError: StateSetter<ChatCallError | null>;
  setActionError: StateSetter<ChatCallError | null>;
  setCanReconnect: StateSetter<boolean>;
  applyThread: ApplyThread;
  drive: DriveStream;
  resetFrontendTools: () => void;
  resetApprovalOutcome: () => void;
}) {
  const creatingRef = useRef<Promise<string | null> | null>(null);
  const requestedConversationRef = useRef(conversationId);
  requestedConversationRef.current = conversationId;

  const openConversation = useCallback(
    async (id: string) => {
      abortRef.current?.abort();
      abortRef.current = null;
      activeIdRef.current = id;
      setActiveId(id);
      setThreadState("loading");
      setThreadError(null);
      setActionError(null);
      setCanReconnect(false);
      resetApprovalOutcome();
      resetFrontendTools();
      const result = await fetchConversation(id);
      if (activeIdRef.current !== id) return;
      if (!result.ok) {
        const liveRun = recallRun(id);
        if (liveRun) {
          applyThread(() => newThreadState());
          setThreadState("ready");
          void drive(id, (signal) => openRunStream(id, liveRun, null, signal)).then(() => {
            const outcome = threadRef.current.outcome;
            if (outcome !== "running" && outcome !== "interrupted") forgetRun(id);
          });
          return;
        }
        setThreadError(result.error);
        setThreadState("error");
        return;
      }
      applyThread(() => hydrateThread(result.value));
      setThreadState("ready");
      const runId = result.value.activeTurn?.runId;
      if (runId) void drive(id, (signal) => openRunStream(id, runId, null, signal));
    },
    [
      abortRef,
      activeIdRef,
      applyThread,
      drive,
      resetApprovalOutcome,
      resetFrontendTools,
      setActionError,
      setActiveId,
      setCanReconnect,
      setThreadError,
      setThreadState,
      threadRef,
    ],
  );

  const ensureConversation = useCallback(async (): Promise<string | null> => {
    const open = activeIdRef.current;
    if (open) return open;
    const pending = creatingRef.current;
    if (pending) return pending;
    const requestedAtStart = requestedConversationRef.current;
    const inFlight = (async (): Promise<string | null> => {
      const created = await createConversation(locale);
      if (requestedConversationRef.current !== requestedAtStart) return null;
      if (!created.ok) {
        setActionError(created.error);
        return null;
      }
      const id = created.value.id;
      activeIdRef.current = id;
      setActiveId(id);
      applyThread(() => newThreadState());
      setThreadState("ready");
      onConversationCreated?.(id);
      return id;
    })();
    creatingRef.current = inFlight;
    void inFlight.finally(() => {
      if (creatingRef.current === inFlight) creatingRef.current = null;
    });
    return inFlight;
  }, [
    activeIdRef,
    applyThread,
    locale,
    onConversationCreated,
    setActionError,
    setActiveId,
    setThreadState,
  ]);

  useEffect(() => {
    if (conversationId === activeIdRef.current) return;
    if (conversationId) {
      void openConversation(conversationId);
      return;
    }
    abortRef.current?.abort();
    abortRef.current = null;
    activeIdRef.current = null;
    setActiveId(null);
    applyThread(() => newThreadState());
    setThreadState("ready");
    setThreadError(null);
    setActionError(null);
    setCanReconnect(false);
    resetApprovalOutcome();
    resetFrontendTools();
  }, [
    abortRef,
    activeIdRef,
    applyThread,
    conversationId,
    openConversation,
    resetApprovalOutcome,
    resetFrontendTools,
    setActionError,
    setActiveId,
    setCanReconnect,
    setThreadError,
    setThreadState,
  ]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [abortRef],
  );

  return { ensureConversation };
}
